import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { BigButton } from '../components/BigButton';
import { AiModeIntroBanner } from '../components/AiModeIntroBanner';
import { useWorksheet } from '../context/WorksheetContext';
import { useAuth } from '../context/AuthContext';
import { FREE_USES } from '../lib/access';
import { isActiveSchoolSeat } from '../lib/school';
import { SchoolSeatBanner } from '../components/SchoolSeatBanner';
import { IconCamera, IconClock, IconBook, IconMic, IconSparkle, IconLock, IconArrowRight, IconGrid } from '../components/Icons';

// The /app home screen. Welcome, the primary call to action, plus the tool
// cards that hint at the wider My Dyslexia Tools portfolio.
//
// Sarah's strategic ask: "create a suite eventually to cross-sell to". The home
// screen is the first surface where future tools appear, so the architecture is
// in place from day one even though only Answer Assist is active in v1.
export function HomeScreen() {
  const navigate = useNavigate();
  const { reset } = useWorksheet();
  const { isAdmin, isSchoolAdmin, subscription, schoolSeat, freeUsesRemaining } = useAuth();

  function startWorksheet() {
    reset();
    navigate('/app/capture');
  }

  const hasActiveSub = subscription && (subscription.status === 'active' || subscription.status === 'trialing');
  const hasSchoolSeat = isActiveSchoolSeat(schoolSeat);
  const showFreeUsesBanner = !isAdmin && !hasActiveSub && !hasSchoolSeat && !isSchoolAdmin;

  return (
    <AppShell>
      <AiModeIntroBanner />
      <SchoolSeatBanner />
      {showFreeUsesBanner && (
        <FreeUsesBanner remaining={freeUsesRemaining} onSubscribe={() => navigate('/app/paywall')} />
      )}
      <div className="px-5 pt-2">
        {/* Hero */}
        <section className="py-6">
          <p className="text-xs uppercase tracking-wider text-ink/40">Welcome back</p>
          <h1 className="mt-1 text-2xl font-medium text-ink leading-snug">
            What would you like to do today?
          </h1>
        </section>

        {/* Primary tool */}
        <section>
          <ToolCard
            title="Answer Assist"
            subtitle="Photo or upload any worksheet, dictate or type your answers, save as PDF."
            Icon={IconCamera}
            available
            primary
            cta="New worksheet"
            onClick={startWorksheet}
          />
        </section>

        {/* Coming-soon tools - hint at the portfolio. Locked but visible. */}
        <section className="mt-6">
          <p className="text-xs uppercase tracking-wider text-ink/40 mb-2 px-1">More tools soon</p>
          <div className="flex flex-col gap-3">
            <ToolCard
              title="Read To Me"
              subtitle="Photo a page, the app reads it aloud with dyslexia-friendly highlighting."
              Icon={IconBook}
              comingSoon
            />
            <ToolCard
              title="Spelling Buddy"
              subtitle="Dictate a word, see how to spell it with phonics breakdown."
              Icon={IconMic}
              comingSoon
            />
            <ToolCard
              title="Word Bank"
              subtitle="Tap any word, get a definition and pronunciation."
              Icon={IconSparkle}
              comingSoon
            />
          </div>
        </section>

        {/* School dashboard card - SENCOs / school admins only */}
        {isSchoolAdmin && (
          <section className="mt-6">
            <button
              onClick={() => navigate('/app/school')}
              className="w-full flex items-center justify-between p-4 rounded-2xl bg-white border border-accent/20 hover:border-accent hover:bg-accentSoft/40 transition"
            >
              <span className="flex items-center gap-3 min-w-0">
                <span className="w-10 h-10 rounded-full bg-accentSoft text-accent grid place-items-center">
                  <IconGrid size={18} />
                </span>
                <span className="text-left min-w-0">
                  <span className="block text-sm font-medium text-ink">School dashboard</span>
                  <span className="block text-xs text-ink/55 leading-snug">Add pupils, manage seats, see usage.</span>
                </span>
              </span>
              <span className="text-ink/30 flex-shrink-0">
                <IconArrowRight size={18} />
              </span>
            </button>
          </section>
        )}

        {/* Exam Mode card - locked attempt for real exams */}
        <section className="mt-6">
          <button
            onClick={() => navigate('/app/exam/start')}
            className="w-full flex items-center justify-between p-4 rounded-2xl bg-white border border-red-300 hover:border-red-500 hover:bg-red-50/40 transition group"
          >
            <span className="flex items-center gap-3 min-w-0">
              <span className="w-10 h-10 rounded-full bg-red-100 text-red-700 grid place-items-center">
                <IconLock size={18} />
              </span>
              <span className="text-left min-w-0">
                <span className="block text-sm font-medium text-ink">Exam Mode</span>
                <span className="block text-xs text-ink/55 leading-snug">
                  Locked attempt for real exams. AI helpers off, full audit trail.
                </span>
              </span>
            </span>
            <span className="text-ink/30 flex-shrink-0">
              <IconArrowRight size={18} />
            </span>
          </button>
        </section>

        {/* Recent activity teaser */}
        <section className="mt-4">
          <button
            onClick={() => navigate('/app/history')}
            className="w-full flex items-center justify-between p-4 rounded-2xl bg-white/70 border border-ink/5 hover:bg-white transition"
          >
            <span className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-accentSoft text-accent grid place-items-center">
                <IconClock size={18} />
              </span>
              <span className="text-left">
                <span className="block text-sm font-medium text-ink">History</span>
                <span className="block text-xs text-ink/50">Your saved worksheets</span>
              </span>
            </span>
            <span className="text-ink/30">
              <IconArrowRight size={18} />
            </span>
          </button>
        </section>
      </div>
    </AppShell>
  );
}

interface ToolCardProps {
  title: string;
  subtitle: string;
  Icon: (props: { size?: number }) => ReactNode;
  available?: boolean;
  comingSoon?: boolean;
  primary?: boolean;
  cta?: string;
  onClick?: () => void;
}

function FreeUsesBanner({ remaining, onSubscribe }: { remaining: number; onSubscribe: () => void }) {
  // Only surface the counter once a user is genuinely close to the paywall.
  // The product is marketed as free, so we don't want a "X of 10 remaining"
  // pill following every casual user round the home screen from day one.
  // Show the gentle banner from 3 worksheets remaining; the firm one when none left.
  if (remaining > 3) return null;
  if (remaining > 0) {
    return (
      <section className="mx-5 mt-4 rounded-2xl bg-accentSoft border border-accent/15 p-4 flex items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wider text-accent font-medium">Almost there</p>
          <p className="mt-0.5 text-sm font-medium text-ink">
            {remaining} free {remaining === 1 ? 'worksheet' : 'worksheets'} left
          </p>
          <p className="mt-0.5 text-xs text-ink/60">
            Unlock unlimited worksheets to keep your routine going.
          </p>
        </div>
        <button
          onClick={onSubscribe}
          className="flex-shrink-0 inline-flex items-center px-3 py-2 rounded-full text-xs font-medium text-accent border border-accent/30 hover:bg-accent hover:text-white transition"
        >
          Unlock
        </button>
      </section>
    );
  }
  return (
    <section className="mx-5 mt-4 rounded-2xl bg-accent text-white p-4 flex items-center justify-between gap-3">
      <div>
        <p className="text-xs uppercase tracking-wider text-white/70 font-medium">Time to unlock</p>
        <p className="mt-0.5 text-sm font-medium">
          You've finished all {FREE_USES} free worksheets. Unlock unlimited to keep going.
        </p>
      </div>
      <button
        onClick={onSubscribe}
        className="flex-shrink-0 inline-flex items-center px-3 py-2 rounded-full text-xs font-medium bg-white text-accent hover:bg-cream transition"
      >
        Unlock
      </button>
    </section>
  );
}

function ToolCard({ title, subtitle, Icon, available, comingSoon, primary, cta, onClick }: ToolCardProps) {
  if (primary) {
    return (
      <div className="rounded-3xl bg-gradient-to-br from-accent to-accentDark text-white p-5 shadow-xl shadow-accent/20">
        <div className="flex items-start gap-3">
          <span className="w-11 h-11 rounded-2xl bg-white/15 grid place-items-center text-white">
            <Icon size={22} />
          </span>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-medium leading-tight">{title}</h2>
            <p className="mt-1 text-sm text-white/85 leading-relaxed">{subtitle}</p>
          </div>
        </div>
        <div className="mt-5">
          <BigButton
            onClick={onClick}
            variant="secondary"
            className="!bg-white !text-accent !border-white shadow-md hover:!brightness-100"
            aria-label={cta}
          >
            {cta}
          </BigButton>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={onClick}
      disabled={!available}
      className="w-full flex items-center gap-3 p-4 rounded-2xl bg-white/70 border border-ink/5 text-left disabled:cursor-default disabled:opacity-90 hover:bg-white transition group"
    >
      <span className="w-11 h-11 rounded-2xl bg-accentSoft text-accent grid place-items-center flex-shrink-0">
        <Icon size={22} />
      </span>
      <span className="flex-1 min-w-0">
        <span className="flex items-center gap-2">
          <span className="text-sm font-medium text-ink">{title}</span>
          {comingSoon && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-ink/40 bg-ink/5 px-2 py-0.5 rounded-full">
              <IconLock size={10} />
              Soon
            </span>
          )}
        </span>
        <span className="block mt-0.5 text-xs text-ink/50 leading-relaxed">{subtitle}</span>
      </span>
    </button>
  );
}
